// SendSMSModal.js - Modal för att skicka SMS till patient
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../UserContext';
import apiClient from '../../apiClient';
import TextAreaWithPhrases from '../TextAreaWithPhrases';
import './SendSMSModal.css';

const SendSMSModal = ({ patient, onClose, onSuccess }) => {
  const { user } = useContext(UserContext);
  const useInternalJournal = user?.clinic?.use_internal_journal || false;

  const [phone, setPhone] = useState(patient?.phone || '');
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Hämta SMS-mallar
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await apiClient.get('/api/sms/templates');
        setTemplates(response.data || []);
      } catch (err) {
        console.error('Error fetching SMS templates:', err);
        // Inte kritiskt - användaren kan fortfarande skriva fritext
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  // Hantera mallval
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);

    if (templateId) {
      const template = templates.find(t => t.template_id.toString() === templateId);
      if (template) {
        setMessage(template.body);
      }
    }
  };

  // Skicka SMS
  const handleSend = async () => {
    if (!phone.trim()) {
      setError('Ange ett telefonnummer');
      return;
    }

    if (!message.trim()) {
      setError('Skriv ett meddelande');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post('/api/sms/send', {
        patient_id: patient.patient_id,
        phone: phone,
        message: message,
        template_id: selectedTemplate || null
      });

      setSuccess(`SMS skickat till ${response.data.phone} (${response.data.sms_count} SMS)`);

      // Stäng efter kort delay
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error sending SMS:', err);
      setError(err.response?.data?.msg || 'Kunde inte skicka SMS');
    } finally {
      setLoading(false);
    }
  };

  // Teckenräknare
  const charCount = message.length;
  const smsCount = Math.ceil(charCount / 160) || 1;

  return (
    <div className="sms-modal-overlay" onClick={onClose}>
      <div className="sms-modal" onClick={e => e.stopPropagation()}>
        <div className="sms-modal-header">
          <h3>
            <i className="fas fa-sms"></i> Skicka SMS
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="sms-modal-body">
          {/* Patient info */}
          <div className="patient-info">
            <span className="patient-name">
              {patient.firstname} {patient.lastname}
            </span>
            <span className="patient-pnr">{patient.personnummer}</span>
          </div>

          {/* Telefonnummer */}
          <div className="form-group">
            <label>
              <i className="fas fa-phone"></i> Telefonnummer
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07X XXX XX XX"
              disabled={loading}
            />
          </div>

          {/* Mall-dropdown */}
          <div className="form-group">
            <label>
              <i className="fas fa-file-alt"></i> Mall (valfritt)
            </label>
            <select
              value={selectedTemplate}
              onChange={handleTemplateChange}
              disabled={loading || loadingTemplates}
            >
              <option value="">-- Välj mall eller skriv fritext --</option>
              {templates.map(template => (
                <option key={template.template_id} value={template.template_id}>
                  {template.template_name}
                </option>
              ))}
            </select>
          </div>

          {/* Meddelande */}
          <div className="form-group">
            <label>
              <i className="fas fa-comment"></i> Meddelande
            </label>
            {useInternalJournal ? (
              <TextAreaWithPhrases
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Skriv ditt meddelande här..."
                rows={5}
                disabled={loading}
              />
            ) : (
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Skriv ditt meddelande här..."
                rows={5}
                disabled={loading}
              />
            )}
            <div className="char-counter">
              <span className={charCount > 160 ? 'warning' : ''}>
                {charCount} tecken
              </span>
              <span className="sms-count">
                ({smsCount} SMS)
              </span>
            </div>
          </div>

          {/* Variabler-hjälp */}
          <div className="variables-help">
            <details>
              <summary>
                <i className="fas fa-info-circle"></i> Tillgängliga variabler
              </summary>
              <div className="variables-list">
                <code>{'{firstname}'}</code> - Förnamn<br />
                <code>{'{lastname}'}</code> - Efternamn<br />
                <code>{'{patientnamn}'}</code> - Fullständigt namn<br />
                <code>{'{kliniknamn}'}</code> - Klinikens namn<br />
                <code>{'{behandlare}'}</code> - Behandlarens namn
              </div>
            </details>
          </div>

          {/* Felmeddelande */}
          {error && (
            <div className="sms-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="sms-success">
              <i className="fas fa-check-circle"></i> {success}
            </div>
          )}
        </div>

        <div className="sms-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Avbryt
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={loading || !phone.trim() || !message.trim()}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Skickar...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i> Skicka SMS
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendSMSModal;
